import React, { FC, useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useOktaAuth } from '@okta/okta-react'
import * as yup from 'yup'
import { routes, getAuthClient } from 'config'
import {
  constants,
  processErrorMsg,
  removeFocusFromField,
  storage,
} from 'utils'
import { BaseLayout, Loader, Module } from 'components'
import { MfaOtpForm } from './components'
import MfaFactorSelect from './components/mfa-factor-select'
import useStyles from './mfa-style'

const MFA: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const state = location?.state as {
    loginPassword: string
  }
  const loginPassword = state?.loginPassword ?? ''
  const { oktaAuth } = useOktaAuth()
  const [transaction, setTransaction] = useState<any | {}>({})
  const [showLoader, setShowLoader] = useState(false)
  const [serviceError, setServiceError] = useState('')
  const [count, setCount] = useState(5)
  const [smsWasEnrolled, setSmsWasEnrolled] = useState(false)
  const [showFactorSelect, setShowFactorSelect] = useState(false)
  const [availableFactorCount, setAvailableFactorCount] = useState(0)
  const { classes } = useStyles()

  useEffect(() => {
    let authClient = getAuthClient()

    if (authClient.tx.exists()) {
      setShowLoader(true)

      authClient.tx
        .resume()
        .then((response: any) => {
          storage.setUser(response?.user?.profile?.login)
          setShowLoader(false)
          setTransaction(response)
        })
        .catch((e: any) => processError(e, 'MFA', 'Resuming transaction'))
    } else {
      navigate(routes.login)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate])

  useEffect(() => {
    if (
      transaction &&
      transaction.status === constants.OKTA_TRANSACTION_STATUS.MFA_REQUIRED
    ) {
      localStorage.setItem('COUNT', JSON.stringify(count))

      // Get all available factors
      const factors = transaction.factors || []
      const smsFactor = factors.find((value: any) => value.factorType === 'sms')
      const emailFactor = factors.find(
        (value: any) => value.factorType === 'email',
      )
      const totpFactor = factors.find(
        (value: any) =>
          value.factorType === 'token:software:totp' ||
          value.factorType === 'totp',
      )

      // Track if SMS was enrolled for later check
      if (smsFactor) {
        setSmsWasEnrolled(true)
      }

      // Count available factors
      const availableFactors = [smsFactor, emailFactor, totpFactor].filter(
        Boolean,
      )
      setAvailableFactorCount(availableFactors.length)

      if (availableFactors.length === 0) {
        navigate(routes.enroll)
        return
      }

      // If multiple factors available, show selection screen
      if (availableFactors.length > 1) {
        setShowFactorSelect(true)
        return
      }

      // Only one factor available, auto-select it
      const factor = availableFactors[0]
      verifyFactor(factor)
    } else if (
      transaction.status ===
        constants.OKTA_TRANSACTION_STATUS.PASSWORD_EXPIRED ||
      transaction.status === constants.OKTA_TRANSACTION_STATUS.PASSWORD_WARN
    ) {
      // password is expiring soon (PASSWORD_WARN) or password already expired, let password-expired page handle both cases
      navigate(routes.passwordExpired, {
        state: { loginPassword },
      })
    } else if (
      transaction.status === constants.OKTA_TRANSACTION_STATUS.LOCKED_OUT
    ) {
      setServiceError('E0000069')
    } else if (
      transaction.status === constants.OKTA_TRANSACTION_STATUS.MFA_ENROLL
    ) {
      // User needs to enroll in MFA, redirect to enrollment page
      navigate(routes.enroll)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction])

  const verifyFactor = (factor: any) => {
    setShowLoader(true)
    setShowFactorSelect(false)
    factor
      .verify({
        rememberDevice: true,
      })
      .then((response: any) => {
        processSuccess(response)
      })
      .catch(processError)
  }

  const handleFactorSelect = (factor: any) => {
    verifyFactor(factor)
  }

  const handleGoBackToFactorSelect = async () => {
    if (transaction.prev) {
      setShowLoader(true)
      try {
        const response = await transaction.prev()
        setShowLoader(false)
        setTransaction(response)
        setShowFactorSelect(true)
      } catch (e: any) {
        setShowLoader(false)
        processError(e, 'MFA', 'Going back to factor selection')
      }
    }
  }

  const processSuccess = (response: any) => {
    setShowLoader(false)
    setTransaction(response)
  }
  const decreaseCount = () => {
    let localCount: any = localStorage.getItem('COUNT')
    if (JSON.parse(localCount) >= 2) {
      setCount(JSON.parse(localCount) - 1)

      setTimeout(() => {
        localStorage.setItem('COUNT', JSON.stringify(--localCount))
      }, 700)
    } else {
      localStorage.removeItem('COUNT')
    }
  }

  const processError = async (
    e: any,
    module: string,
    moduleDescription: string = '',
  ) => {
    //APT-1222: Fixing error cases when MFA code is outdated
    if (e.toString().includes('Invalid token provided')) {
      await oktaAuth.signOut({}).then(() => {
        localStorage.removeItem('okta-cache-storage')
        localStorage.removeItem('okta-original-uri-storage')
        localStorage.removeItem('okta-shared-transaction-storage')
      })
    }

    setShowLoader(false)
    setServiceError(processErrorMsg(e, module, moduleDescription))
  }

  const handleSubmit = (values: any, { setSubmitting }: any) => {
    removeFocusFromField()
    setSubmitting(true)
    setServiceError('')
    setShowLoader(true)
    switch (transaction.status) {
      case constants.OKTA_TRANSACTION_STATUS.MFA_REQUIRED:
        localStorage.setItem('COUNT', JSON.stringify(count))
        // Try SMS first, then fall back to any available factor
        const smsFactor = transaction.factors?.find(
          (value: any) => value.factorType === 'sms',
        )
        const emailFactor = transaction.factors?.find(
          (value: any) => value.factorType === 'email',
        )
        const totpFactor = transaction.factors?.find(
          (value: any) =>
            value.factorType === 'token:software:totp' ||
            value.factorType === 'totp',
        )
        const factor = smsFactor || emailFactor || totpFactor

        if (!factor) {
          setShowLoader(false)
          setSubmitting(false)
          navigate(routes.enroll)
          return
        }
        factor
          .verify({
            rememberDevice: true,
          })
          .then(processSuccess)
          .catch(processError)
          .then(() => setSubmitting(false))
        break
      case constants.OKTA_TRANSACTION_STATUS.MFA_CHALLENGE:
        decreaseCount()
        transaction
          .verify({
            rememberDevice: true,
            passCode: values.verificationCode,
          })
          .then(processSuccess)
          .catch(processError)
          .then(() => setSubmitting(false))
        break
      case constants.OKTA_TRANSACTION_STATUS.MFA_ENROLL_ACTIVATE:
        decreaseCount()
        transaction
          .activate({
            passCode: values.verificationCode,
          })
          .then(processSuccess)
          .catch(processError)
          .then(() => setSubmitting(false))
        break
    }
  }

  const handleResend = (event: any) => {
    event.stopPropagation()
    event.preventDefault()
    try {
      transaction.resend('email')
    } catch (e) {
      processError(e, 'MFA', 'Resending MFA OTP code')
    }
  }

  if (transaction.sessionToken) {
    // Check if SMS was enrolled when we started the MFA flow
    if (!smsWasEnrolled) {
      // User verified with non-SMS factor but needs to enroll SMS
      // Store the session token and redirect to enroll SMS
      localStorage.setItem('pendingSessionToken', transaction.sessionToken)
      navigate(routes.enroll, {
        state: {
          requireSmsEnrollment: true,
          sessionToken: transaction.sessionToken,
        },
      })
      return null
    }

    oktaAuth.signInWithRedirect({ sessionToken: transaction.sessionToken })
    return null
  }

  // Check if we should show factor selection
  if (
    showFactorSelect &&
    transaction.status === constants.OKTA_TRANSACTION_STATUS.MFA_REQUIRED
  ) {
    const factors = transaction.factors || []
    return (
      <BaseLayout boxLayoutClassName={classes.mfaContainer}>
        <Loader visible={showLoader} />
        <MfaFactorSelect
          factors={factors}
          onSelectFactor={handleFactorSelect}
          serviceError={serviceError}
        />
      </BaseLayout>
    )
  }

  // Check if we have multiple factors and can go back
  const hasMultipleFactors = () => {
    if (
      transaction.status !== constants.OKTA_TRANSACTION_STATUS.MFA_CHALLENGE
    ) {
      return false
    }
    // Only show "use different method" if there are actually multiple factors
    // and we can go back to select a different one
    return availableFactorCount > 1 && typeof transaction.prev === 'function'
  }

  let formContent: any
  let validationSchema: any
  let initialValues: any
  switch (transaction.status) {
    case constants.OKTA_TRANSACTION_STATUS.MFA_CHALLENGE:
      initialValues = {
        verificationCode: '',
      }

      validationSchema = yup.object().shape({
        verificationCode: yup.string().required('Required').length(6),
      })

      formContent = (
        <MfaOtpForm
          initialValues={initialValues}
          validationSchema={validationSchema}
          handleSubmit={handleSubmit}
          serviceError={serviceError}
          handleResend={handleResend}
          transaction={transaction}
          count={count}
          setShowLoader={setShowLoader}
          onUseDifferentMethod={
            hasMultipleFactors() ? handleGoBackToFactorSelect : undefined
          }
        />
      )
      break
    case constants.OKTA_TRANSACTION_STATUS.MFA_ENROLL_ACTIVATE:
      validationSchema = yup.object().shape({
        verificationCode: yup.string().required('Required').length(6),
      })
      initialValues = {
        verificationCode: '',
      }

      formContent = (
        <MfaOtpForm
          transaction={transaction}
          initialValues={initialValues}
          handleSubmit={handleSubmit}
          serviceError={serviceError}
          validationSchema={validationSchema}
          count={count}
          handleResend={handleResend}
          setShowLoader={setShowLoader}
        />
      )
      break
  }

  return (
    <BaseLayout boxLayoutClassName={classes.mfaContainer}>
      <Loader visible={showLoader} />
      {formContent}
      {!formContent && serviceError && (
        <Module isModuleInitialized={true} serviceError={serviceError} />
      )}
    </BaseLayout>
  )
}

export default MFA
